import { useCallback, useMemo, useState } from "react";
import { danger, modalHandler, success, useSearch } from "@";
import { useCanShow } from "@/shared/hooks";
import { reSendOrder } from "../api/api";
import { useGiftOrdersQuery } from "../api/queries";
import { useUpdateGiftOrderNamesMutation } from "../api/mutations";
import { searchValues } from "../lib/options";
import { createTableColumns } from "../components/TableOptions";
import type {
  GiftOrder,
  PaginationState,
  QueryParams,
  SearchValues,
} from "../types";
import type { ColumnDef } from "@tanstack/react-table";
import TableActions from "../components/TableActions";

interface ResendOrderState {
  isResendOrder: boolean;
  orderItemId: number | null;
}

interface UseGiftOrdersPageReturn {
  data: GiftOrder[];
  columns: ColumnDef<GiftOrder>[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  search: { show: boolean; values: SearchValues };
  updateSearch: (params: { show?: boolean; values?: SearchValues }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  pg: PaginationState;
  updatePg: (newPg: Partial<PaginationState>) => void;
  refetch: () => void;
  isSmsBalanceLow: boolean;
  getSmsBalance: (value: boolean) => void;
  attribute: Partial<GiftOrder>;
}

export const useGiftOrdersPage = (): UseGiftOrdersPageReturn => {
  const { search, updateSearch } = useSearch(searchValues);
  const [pg, setPg] = useState<PaginationState>({ page: 1, size: 10 });
  const [appliedSearch, setAppliedSearch] =
    useState<SearchValues>(searchValues);

  const [isSmsBalanceLow, setIsSmsBalanceLow] = useState(true);
  const [resendOrder, setResendOrder] = useState<ResendOrderState>({
    isResendOrder: false,
    orderItemId: null,
  });
  const [attribute, setAttribute] = useState<Partial<GiftOrder>>({});

  const canEditNames = useCanShow("editGiftProject");
  const updateNamesMutation = useUpdateGiftOrderNamesMutation();

  const queryParams: QueryParams = useMemo(
    () => ({ page: pg.page, perSize: pg.size, search: appliedSearch }),
    [pg.page, pg.size, appliedSearch],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useGiftOrdersQuery(queryParams);

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  const updatePg = useCallback((newPg: Partial<PaginationState>) => {
    setPg((prev) => ({ ...prev, ...newPg }));
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setAppliedSearch(search.values);
    setPg((prev) => ({ ...prev, page: 1 }));
  }, [search.values]);

  const handleSearchReset = useCallback(() => {
    updateSearch({ values: searchValues });
    setAppliedSearch(searchValues);
    setPg((prev) => ({ ...prev, page: 1 }));
  }, [updateSearch]);

  const handleResendOrder = useCallback(async (orderItemId: number) => {
    setResendOrder({ isResendOrder: true, orderItemId });
    try {
      await reSendOrder(orderItemId);
      success("تم إعادة الإرسال بنجاح");
    } catch (err) {
      console.error(err);
    }
    setResendOrder({ isResendOrder: false, orderItemId: null });
  }, []);

  const getSmsBalance = useCallback(
    (value: boolean) => setIsSmsBalanceLow(value),
    [],
  );

  const reDesignAttribute = useCallback((row: GiftOrder) => {
    setAttribute(row);
    modalHandler();
  }, []);

  const handleSaveName = useCallback(
    async (
      row: GiftOrder,
      patch: Partial<Pick<GiftOrder, "giftSenderName" | "giftRecieverName">>,
    ) => {
      try {
        await updateNamesMutation.mutateAsync({
          orderItemId: row.orderItemId,
          giftSenderName: patch.giftSenderName ?? row.giftSenderName ?? "",
          giftRecieverName:
            patch.giftRecieverName ?? row.giftRecieverName ?? "",
        });
        await success("تم الحفظ");
      } catch (err) {
        danger("فشل الحفظ");
        throw err;
      }
    },
    [updateNamesMutation],
  );

  const columns = useMemo(
    () =>
      [
        ...createTableColumns({
          canEdit: canEditNames,
          onSaveName: handleSaveName,
        }),
        {
          id: "actions",
          header: "الخيارات",
          size: 110,
          cell: (info: { row: { original: GiftOrder } }) => (
            <TableActions
              row={info.row.original}
              reSend={handleResendOrder}
              isSmsBalanceLow={isSmsBalanceLow}
              reDesignAttribute={reDesignAttribute}
              orderItemId={resendOrder.orderItemId}
              isResendOrder={resendOrder.isResendOrder}
            />
          ),
        },
      ] as ColumnDef<GiftOrder>[],
    [
      canEditNames,
      handleSaveName,
      handleResendOrder,
      isSmsBalanceLow,
      reDesignAttribute,
      resendOrder.orderItemId,
      resendOrder.isResendOrder,
    ],
  );

  return {
    data,
    columns,
    isLoading,
    error: error as Error | null,
    totalItems,
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
    pg,
    updatePg,
    refetch,
    isSmsBalanceLow,
    getSmsBalance,
    attribute,
  };
};
